import React, { useEffect, useMemo, useRef, useState } from "react";
import { playGameSound } from "../../services/gameSounds";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase/firebaseConfig";
import { buildReturnPath } from "../../utils/buildReturnPath";
import { launchConfetti } from "../../utils/confetti";
import "./MemoryMatchGame.css";
import { useLeaveGame } from "./gameExit";
import "./kit/quizkit.css"; // .qk-scene: the shared game background (no pirate ship — that's the bonus round's)
import "./kit/classicGames.css"; // the new look for this game (buttons, cards, score chips)

const backgroundMusicUrl =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gamemusic%2Fmemory-matchgame.mp3?alt=media&token=7278a1d1-0442-400e-8567-791647968b72";

const defaultBackground =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fpirate-ship-background.png?alt=media&token=3912623d-30d2-468a-ab2e-449398d2bce2";

const shuffleArray = (array) => [...array].sort(() => Math.random() - 0.5);

const getCardLabel = (card) => {
  return card.text || card.targetText || "";
};

const MemoryMatchGame = ({
  questions = [],
  onComplete,
  placeholderImage,
  user,
  topic,
}) => {
  const navigate = useNavigate();
  const leaveGame = useLeaveGame(); // back to the subject page this game came from
  const { subject } = useParams();

  const [cards, setCards] = useState([]);
  const [flippedIds, setFlippedIds] = useState([]);
  const [matchedIds, setMatchedIds] = useState([]);
  const [score, setScore] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  const musicRef = useRef(null);

  // Guards against onComplete firing more than once. The effect depends on
  // `matchedIds`, which is a new array on every render, and `onComplete` isn't
  // in the dependency list at all — so any re-render after the last match
  // could fire the hand-off again and overwrite the score in Firestore.
  const completedRef = useRef(false);

  const sourceOptions = useMemo(() => {
    const firstQuestion = questions?.[0] || {};
    return Array.isArray(firstQuestion.options) ? firstQuestion.options : [];
  }, [questions]);

  useEffect(() => {
    const builtCards = sourceOptions.flatMap((option, index) => {
      const matchId = option.id || `match-${index + 1}`;

      return [
        {
          cardId: `${matchId}-item`,
          matchId,
          type: "item",
          text: option.text || "",
          image: option.image || "",
        },
        {
          cardId: `${matchId}-target`,
          matchId,
          type: "target",
          text: option.targetText || "",
          image: option.targetImage || "",
        },
      ];
    });

    setCards(shuffleArray(builtCards));
    setFlippedIds([]);
    setMatchedIds([]);
    setScore(0);
    completedRef.current = false;
  }, [sourceOptions]);

  useEffect(() => {
    musicRef.current = new Audio(backgroundMusicUrl);
    musicRef.current.loop = true;
    musicRef.current.volume = 0.3;
    musicRef.current.play().catch(() => {});

    return () => {
      if (musicRef.current) {
        musicRef.current.pause();
        musicRef.current.currentTime = 0;
      }
    };
  }, []);

  const handleReturnHome = () => leaveGame();

  const handleCardClick = (card) => {
    if (
      flippedIds.includes(card.cardId) ||
      matchedIds.includes(card.cardId) ||
      flippedIds.length === 2
    ) {
      return;
    }

    const nextFlipped = [...flippedIds, card.cardId];
    setFlippedIds(nextFlipped);

    if (nextFlipped.length === 2) {
      const first = cards.find((c) => c.cardId === nextFlipped[0]);
      const second = cards.find((c) => c.cardId === nextFlipped[1]);

      const isMatch =
        first &&
        second &&
        first.matchId === second.matchId &&
        first.cardId !== second.cardId;

      if (isMatch) {
        playGameSound("correct", "memoryMatch");
        setTimeout(() => {
          setMatchedIds((prev) => [...prev, first.cardId, second.cardId]);
          setScore((prev) => prev + 10);
          setFlippedIds([]);
        }, 600);
      } else {
        playGameSound("wrong", "memoryMatch");
        setTimeout(() => {
          setFlippedIds([]);
        }, 900);
      }
    }
  };

  useEffect(() => {
    if (completedRef.current) return;

    if (cards.length > 0 && matchedIds.length === cards.length) {
      completedRef.current = true;
      const finalScore = score;

      launchConfetti();

      onComplete?.({
        score: finalScore,
        answers: sourceOptions.map((option) => ({
          question: topic?.title || "Memory Match",
          selectedText: option.text || "[image]",
          correctText: option.targetText || "[image]",
          correct: true,
        })),
        details: sourceOptions,
      });
    }
  }, [matchedIds, cards.length]);

  return (
    <div
      className="memory-match memory-match-wrapper qk-scene"
    >
      <button
        onClick={() => {
          const nextMuted = !isMuted;
          setIsMuted(nextMuted);
          if (musicRef.current) musicRef.current.muted = nextMuted;
        }}
        className="memory-mute-button"
      >
        {isMuted ? "🔇" : "🔊"}
      </button>

      <div className="scoreboard">
        <img
          src="/assets/scoreboard-background.png"
          alt="Scoreboard"
          className="scoreboard-bg"
        />
        <div className="score-overlay">
          <p>Score: {score}</p>
        </div>
      </div>

      <img
        src="/assets/compass-return2.png"
        alt="Return Home"
        className="compass-return"
        onClick={handleReturnHome}
      />

      <div className="memory-match-container">
        <img
          src="https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f"
          alt="SBL Logo"
          className="drag-quiz-logo"
        />

        <h2>🧠 Memory Match</h2>
        <p>Pick two cards. Match the item with its correct target.</p>

        <div className="memory-card-grid">
          {cards.map((card) => {
            const isFlipped =
              flippedIds.includes(card.cardId) ||
              matchedIds.includes(card.cardId);

            return (
              <button
                key={card.cardId}
                type="button"
                className={`memory-card ${isFlipped ? "flipped" : ""}`}
                onClick={() => handleCardClick(card)}
              >
                {!isFlipped ? (
                  <div className="memory-card-back">?</div>
                ) : (
                  <div className="memory-card-front">
                    {card.image && (
                      <img src={card.image} alt={getCardLabel(card)} />
                    )}
                    {getCardLabel(card) && <span>{getCardLabel(card)}</span>}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MemoryMatchGame;